import {inject, Injectable} from "@angular/core";
import {Observable, take} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {environment} from "@env/environment";
import {removeIfNullUndefined} from "@shared/commons/transform.commons";
import {CampaignTaskStatus} from "@core/models/campaign.enums";

@Injectable({
    providedIn: 'root'
})
export class CampaignsApiService {
    private readonly apiServiceUrl = environment.serviceApi;
    private http = inject(HttpClient);

    // --------------------------------------------------------
    // Campaign CRUD endpoints
    // --------------------------------------------------------
    findAll(params?: any): Observable<any> {
        params = removeIfNullUndefined(params);
        return this.http.get(`${this.apiServiceUrl}/v1/campaigns`, {params})
            .pipe(take(1));
    }

    findById(id: number): Observable<any> {
        return this.http.get(`${this.apiServiceUrl}/v1/campaigns/${id}`)
            .pipe(take(1));
    }

    create(campaign: any): Observable<any> {
        return this.http.post(`${this.apiServiceUrl}/v1/campaigns`, campaign)
            .pipe(take(1));
    }

    // --------------------------------------------------------
    // Campaign preview endpoint (with ETag for caching)
    // --------------------------------------------------------
    preview(params: any): Observable<any> {
        params = removeIfNullUndefined(params);
        return this.http.get(`${this.apiServiceUrl}/v1/campaigns/companies/preview`, {params})
            .pipe(take(1));
    }

    // --------------------------------------------------------
    // Campaign task management endpoints
    // --------------------------------------------------------

    /**
     * Get campaign companies grouped by status (for kanban board)
     * GET /campaigns/:id/tasks
     */
    getCampaignCompaniesByStatus(campaignId: number, params?: any): Observable<any> {
        params = removeIfNullUndefined(params);
        return this.http.get(`${this.apiServiceUrl}/v1/campaigns/${campaignId}/tasks`, {params})
            .pipe(take(1));
    }

    /**
     * Get detailed information about a specific company/task in a campaign
     * GET /campaigns/:id/tasks/:taskId
     */
    getCampaignCompanyDetails(campaignId: number, taskId: number): Observable<any> {
        return this.http.get(`${this.apiServiceUrl}/v1/campaigns/${campaignId}/tasks/${taskId}`)
            .pipe(take(1));
    }

    /**
     * Update the status of a task (move to different column in kanban)
     * PATCH /campaigns/:id/tasks/:taskId/status
     */
    updateTaskStatus(campaignId: number, taskId: number, status: CampaignTaskStatus): Observable<any> {
        return this.http.patch(`${this.apiServiceUrl}/v1/campaigns/${campaignId}/tasks/${taskId}/status`, {status})
            .pipe(take(1));
    }

    /**
     * Update task assignee
     * PATCH /campaigns/:id/tasks/:taskId/assignee
     */
    updateTaskAssignee(campaignId: number, taskId: number, assignedTo: any): Observable<any> {
        return this.http.patch(`${this.apiServiceUrl}/v1/campaigns/${campaignId}/tasks/${taskId}/assignee`, {assignedTo})
            .pipe(take(1));
    }

    /**
     * Update task position (reorder)
     * PATCH /campaigns/:id/tasks/:taskId/position
     */
    updateTaskPosition(campaignId: number, taskId: number, payload: {
        newStatus: string;
        afterTaskId: number | null;
        beforeTaskId: number | null;
    }): Observable<any> {
        return this.http.patch(`${this.apiServiceUrl}/v1/campaigns/${campaignId}/tasks/${taskId}/position`, payload)
            .pipe(take(1));
    }

    /**
     * Add a comment to a task
     * POST /campaigns/:id/tasks/:taskId/comments
     */
    addTaskComment(campaignId: number, taskId: number, content: string): Observable<any> {
        return this.http.post(`${this.apiServiceUrl}/v1/campaigns/${campaignId}/tasks/${taskId}/comments`, {content})
            .pipe(take(1));
    }

    /**
     * Get list of users for a campaign (non-paginated)
     * GET /campaigns/:id/users
     */
    getCampaignUsers(): Observable<any> {
        return this.http.get(`${this.apiServiceUrl}/v1/campaigns/users`)
            .pipe(take(1));
    }

    /**
     * Remove companies/tasks from a campaign
     * DELETE /campaigns/:id/companies
     */
    removeCompaniesFromCampaign(campaignId: number, taskIds: number[]): Observable<any> {
        return this.http.delete(`${this.apiServiceUrl}/v1/campaigns/${campaignId}/companies`, {
            body: {taskIds}
        }).pipe(take(1));
    }

    /**
     * Get tasks assigned to the authenticated user
     * GET /my-tasks
     */
    getMyTasks(params?: any): Observable<any> {
        params = removeIfNullUndefined(params);
        return this.http.get(`${this.apiServiceUrl}/v1/my-tasks`, {params})
            .pipe(take(1));
    }

}
